import React, { memo, useCallback } from 'react'
import SwapIcon from 'transfix-icons/build/SwapIcon'
import { IGeocodingResultTypeEnum, Maybe } from 'types/graphqlTypes'
import { IAutocompleteLocationProps, TBaseValue } from '../AutocompleteLocation'
import AutocompleteLocationRadius, {
  IAutocompleteLocationRadiusProps,
  IAutocompleteLocationRadiusValue,
} from '../AutocompleteLocationRadius'
import { IconButton } from '../IconButton'
import { ISelectRadiusProps } from '../SelectRadius'
import {
  useAutocompleteLocationStyles,
  usePickupDropoffSelectStyles,
} from './hooks/usePickupDropoffSelectStyles'

const RESULT_TYPES: IGeocodingResultTypeEnum[] = [IGeocodingResultTypeEnum.City]

export interface IPickupDropoffSelectValue {
  pickup?: IAutocompleteLocationProps['value']
  pickupRadius?: ISelectRadiusProps['value']
  dropoff?: IAutocompleteLocationProps['value']
  dropoffRadius?: ISelectRadiusProps['value']
}

export interface IPickupDropoffSelectErrors {
  pickup?: IAutocompleteLocationProps['inputValidationMessage']
  pickupRadius?: ISelectRadiusProps['validationMessage']
  dropoff?: IAutocompleteLocationProps['inputValidationMessage']
  dropoffRadius?: ISelectRadiusProps['validationMessage']
}

export interface IPickupDropoffSelectProps {
  value: IPickupDropoffSelectValue | null
  onChange: <K extends keyof IPickupDropoffSelectValue, V extends IPickupDropoffSelectValue[K]>(
    key: K,
    newVal: V | null
  ) => void
  onSwap: () => void
  /**
   * @default false
   */
  enableAnyPickup?: boolean
  /**
   * @default false
   */
  enableAnyDropoff?: boolean
  /**
   * @default {}
   */
  errors?: IPickupDropoffSelectErrors | null
}

const PickupDropoffSelect = memo<IPickupDropoffSelectProps>(
  ({ enableAnyDropoff = false, enableAnyPickup = false, errors = {}, onChange, onSwap, value }) => {
    const classes = usePickupDropoffSelectStyles({ enableAnyDropoff, enableAnyPickup })
    const autocompleteClasses = useAutocompleteLocationStyles()

    const { dropoff = null, dropoffRadius, pickup = null, pickupRadius } = value || {}
    const {
      dropoff: dropoffLocationError,
      dropoffRadius: dropoffRadiusError,
      pickup: pickupLocationError,
      pickupRadius: pickupRadiusError,
    } = errors || {}

    const pickupValue: IAutocompleteLocationRadiusValue = { location: pickup, radius: pickupRadius }
    const dropoffValue: IAutocompleteLocationRadiusValue = {
      location: dropoff,
      radius: dropoffRadius,
    }

    const pickupChangeHandler: IAutocompleteLocationRadiusProps['onChange'] = useCallback(
      (key, newVal) => {
        if (key === 'location') {
          onChange('pickup', newVal as Maybe<TBaseValue>)
        } else {
          onChange('pickupRadius', newVal)
        }
      },
      [onChange]
    )

    const dropoffChangeHandler: IAutocompleteLocationRadiusProps['onChange'] = useCallback(
      (key, newVal) => {
        if (key === 'location') {
          onChange('dropoff', newVal as Maybe<TBaseValue>)
        } else {
          onChange('dropoffRadius', newVal)
        }
      },
      [onChange]
    )

    const swapHandler = useCallback(() => {
      if (!enableAnyPickup && !enableAnyDropoff) {
        onSwap()
      } else if (enableAnyDropoff && dropoff) {
        onSwap()
      } else if (enableAnyPickup && pickup) {
        onSwap()
      }
    }, [enableAnyDropoff, enableAnyPickup, pickup, dropoff, onSwap])

    return (
      <div data-test='pickup-dropoff-select' className={classes.container}>
        <AutocompleteLocationRadius
          value={pickupValue}
          onChange={pickupChangeHandler}
          enableAnyLocation={enableAnyPickup}
          anyLocationOptionLabel='Any Pickup'
          dataTest='pickup'
          className={`${classes.inputContainer} pickup-input`}
          classNameRadiusWrapper={classes.radiusSelect}
          AutocompleteLocationProps={{
            placeholder: 'Select Pickup',
            dataTest: 'pickup-autocomplete',
            renderInputType: 'withIcon',
            inputIcon: 'ArrowUp',
            resultTypes: RESULT_TYPES,
            classes: autocompleteClasses,
            inputError: !!pickupLocationError,
            inputValidationMessage: pickupLocationError,
            autoSelect: false,
          }}
          SelectRadiusProps={{
            placeholder: 'Select Radius',
            dataTest: 'pickup-radius',
            large: true,
            error: !!pickupRadiusError,
            validationMessage: pickupRadiusError,
          }}
        />
        <div className={classes.swapButtonWrapper}>
          <IconButton Icon={SwapIcon} onClick={swapHandler} size='large' data-test='swap-button' />
        </div>
        <AutocompleteLocationRadius
          value={dropoffValue}
          onChange={dropoffChangeHandler}
          enableAnyLocation={enableAnyDropoff}
          anyLocationOptionLabel='Any Dropoff'
          dataTest='dropoff'
          className={`${classes.inputContainer} dropoff-input`}
          classNameRadiusWrapper={classes.radiusSelect}
          AutocompleteLocationProps={{
            placeholder: 'Select Dropoff',
            dataTest: 'dropoff-autocomplete',
            renderInputType: 'withIcon',
            inputIcon: 'ArrowDown',
            resultTypes: RESULT_TYPES,
            classes: autocompleteClasses,
            inputError: !!dropoffLocationError,
            inputValidationMessage: dropoffLocationError,
            autoSelect: false,
          }}
          SelectRadiusProps={{
            placeholder: 'Select Radius',
            dataTest: 'dropoff-radius',
            large: true,
            error: !!dropoffRadiusError,
            validationMessage: dropoffRadiusError,
          }}
        />
      </div>
    )
  }
)

export default PickupDropoffSelect
